import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { API } from "@/lib/config";
import { LEGAL } from "@/lib/legal";
import { useUser } from "@/components/contexts/UserContext";
import { clearLocalLibrary } from "@/lib/storage";

/**
 * Public account deletion.
 *
 * Google Play requires two separate things and this page is the second one:
 *
 *   1. an in-app path to delete the account   - Account page, already present
 *   2. a URL a person can open in a browser, without installing the app, to
 *      request the same deletion                - this page
 *
 * That is why it authenticates with email + password against
 * POST /api/auth/delete-account rather than reusing the signed-in bearer token:
 * someone who has uninstalled the app has no token, and Google's reviewer tests
 * the URL cold. The signed-in case is still handled, it just skips the form.
 *
 * The URL submitted in Play Console must be the deployed form of this route,
 * see DELETE_ACCOUNT_URL in src/lib/legal.js.
 */
export const DeleteAccountPage = () => {
  const { isSignedIn, user, deleteAccount } = useUser();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [alsoClearDevice, setAlsoClearDevice] = useState(false);

  const finish = () => {
    if (alsoClearDevice) {
      try {
        clearLocalLibrary();
      } catch {
        /* the account is already gone; a failed local wipe must not look like a failed deletion */
      }
    }
    setDone(true);
  };

  const handleSignedInDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      finish();
    } catch {
      toast.error("Could not delete the account. Please try again or email us.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/delete-account`, { email: email.trim(), password });
      setPassword("");
      finish();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete the account");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4" data-testid="delete-account-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto text-center"
        >
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-black mb-3">Account deleted</h1>
          <p className="text-white/60 text-sm leading-relaxed mb-8">
            Your account, your email address and the watch history, saves and follows
            stored under it have been removed from our servers. Backups are overwritten
            on their normal cycle, within 30 days. Nothing further is required from you.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-heading font-bold"
          >
            Back to TrendingZ
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4" data-testid="delete-account-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        <h1 className="font-heading text-3xl font-black mb-3">Delete my account</h1>
        <p className="text-sm text-white/60 leading-relaxed mb-8">
          This permanently deletes your {LEGAL.appName} account and everything stored
          under it on our servers. You do not need the app installed to do this.
        </p>

        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-8 space-y-3 text-sm">
          <p className="font-semibold text-white/90">What gets deleted</p>
          <ul className="list-disc pl-5 space-y-1.5 text-white/60 marker:text-orange-500/70">
            <li>Your account and email address</li>
            <li>Your password hash</li>
            <li>Your synced watch history, saved videos and followed channels</li>
            <li>Your email digest subscription, if you had one</li>
          </ul>
          <p className="font-semibold text-white/90 pt-2">What is not affected</p>
          <ul className="list-disc pl-5 space-y-1.5 text-white/60 marker:text-orange-500/70">
            <li>
              The copy of your library held on this device. Tick the box below to clear it
              too, or keep it and carry on using the app signed out.
            </li>
            <li>
              Anonymous, aggregated click counts that feed the trending rankings. These
              are never linked to an account and cannot be traced back to you.
            </li>
          </ul>
          <p className="text-white/40 text-xs pt-1">
            Deletion is immediate and cannot be undone. Backups are overwritten within 30 days.
          </p>
        </div>

        <label className="flex items-start gap-3 mb-6 cursor-pointer">
          <input
            type="checkbox"
            checked={alsoClearDevice}
            onChange={(e) => setAlsoClearDevice(e.target.checked)}
            className="mt-1 accent-orange-500 w-4 h-4"
          />
          <span className="text-sm text-white/60">
            Also clear the library stored on this device.
          </span>
        </label>

        {isSignedIn ? (
          <div className="space-y-4">
            <p className="text-sm text-white/60">
              Signed in as <span className="text-white">{user?.email}</span>.
            </p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 accent-orange-500 w-4 h-4"
              />
              <span className="text-sm text-white/60">
                I understand this permanently deletes my account and cannot be undone.
              </span>
            </label>
            <button
              onClick={handleSignedInDelete}
              disabled={!confirmed || busy}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/90 hover:bg-red-500 text-white font-heading font-bold disabled:opacity-40 disabled:hover:bg-red-500/90 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete my account
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-white/60">
              Confirm it is your account by entering the email and password you signed up with.
            </p>
            <div>
              <label htmlFor="delete-email" className="block text-sm text-white/60 mb-2">
                Email
              </label>
              <input
                id="delete-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="delete-password" className="block text-sm text-white/60 mb-2">
                Password
              </label>
              <input
                id="delete-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 accent-orange-500 w-4 h-4"
              />
              <span className="text-sm text-white/60">
                I understand this permanently deletes my account and cannot be undone.
              </span>
            </label>
            <button
              type="submit"
              disabled={!confirmed || busy}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/90 hover:bg-red-500 text-white font-heading font-bold disabled:opacity-40 disabled:hover:bg-red-500/90 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete my account
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-white/10 flex gap-3 text-sm text-white/50">
          <AlertTriangle className="w-4 h-4 text-white/30 shrink-0 mt-0.5" />
          <p>
            Forgotten your password, or the form is not working? Email{" "}
            <a className="text-orange-400 hover:text-orange-300" href={`mailto:${LEGAL.privacyEmail}`}>
              {LEGAL.privacyEmail}
            </a>{" "}
            from the address on the account and we will delete it within 30 days. See our{" "}
            <a className="text-orange-400 hover:text-orange-300" href="/privacy">
              Privacy Policy
            </a>{" "}
            for what we keep and for how long.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DeleteAccountPage;
